import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowLeft, Truck, Shield, RefreshCw, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { publicAPI } from '../../lib/api';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';
import { trackViewContent, trackAddToCart } from '../../lib/tracking';
import Navbar from '../../components/store/Navbar';
import Footer from '../../components/store/Footer';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState('');

  useEffect(() => {
    loadProduct();
  }, [slug]);
  useEffect(() => {
    if (product && selectedVariant) {
      // Track product view
      trackViewContent({
        id: product.id,
        name: product.name,
        category: product.category,
        type: 'product',
        price: selectedVariant.price_cents
      });
    }
  }, [product, selectedVariant]);


  const loadProduct = async () => {
    try {
      const response = await publicAPI.getProduct(slug);
      setProduct(response.data);
      if (response.data.variants?.length > 0) {
        setSelectedVariant(response.data.variants[0]);
      }
      if (response.data.images?.length > 0) {
        setMainImage(response.data.images[0]);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Produit introuvable');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      toast.error('Veuillez sélectionner une variante');
      return;
    }

    try {
      await addItem({
        product_id: product.id,
        variant_id: selectedVariant.id,
        quantity: quantity
      });
      
            // Track add to cart event
            trackAddToCart({
              id: product.id,
              name: product.name,
              type: 'product',
              price: selectedVariant.price_cents * quantity
            });
      
      toast.success('Produit ajouté au panier !');
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Erreur lors de l\'ajout au panier');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Produit introuvable</h1>
            <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <Link to="/looks" className="hover:text-foreground">Produits</Link>
            <span>/</span>
            <span className="text-foreground">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="aspect-square rounded-lg overflow-hidden bg-muted"
              >
                <img
                  src={mainImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              
              {product.images?.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setMainImage(img)}
                      className={`aspect-square rounded-lg overflow-hidden ${
                        mainImage === img ? 'ring-2 ring-primary' : ''
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
                {product.category && (
                  <Badge variant="secondary">{product.category}</Badge>
                )}
              </div>

              <div className="text-3xl font-bold">
                {selectedVariant ? formatPrice(selectedVariant.price_cents) : '—'}
              </div>

              {product.description && (
                <p className="text-muted-foreground">{product.description}</p>
              )}

              {/* Variant Selection */}
              {product.variants?.length > 0 && (
                <div className="space-y-4">
                  {product.variants.some(v => v.size) && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">Taille</label>
                      <Select
                        value={selectedVariant?.id}
                        onValueChange={(id) => {
                          const variant = product.variants.find(v => v.id === id);
                          setSelectedVariant(variant);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir une taille" />
                        </SelectTrigger>
                        <SelectContent>
                          {product.variants.filter(v => v.size).map((variant) => (
                            <SelectItem key={variant.id} value={variant.id}>
                              {variant.size} {variant.color && `- ${variant.color}`}
                              {variant.stock === 0 && ' (Rupture)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {product.variants.some(v => v.color && !v.size) && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">Couleur</label>
                      <div className="flex gap-2">
                        {product.variants.filter(v => v.color && !v.size).map((variant) => (
                          <button
                            key={variant.id}
                            onClick={() => setSelectedVariant(variant)}
                            className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                              selectedVariant?.id === variant.id
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            {variant.color}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="text-sm font-medium mb-2 block">Quantité</label>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </Button>
                  <span className="text-lg font-medium w-12 text-center">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.min(selectedVariant?.stock || 99, quantity + 1))}
                  >
                    +
                  </Button>
                </div>
                {selectedVariant && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {selectedVariant.stock > 0 ? `${selectedVariant.stock} en stock` : 'Rupture de stock'}
                  </p>
                )}
              </div>

              {/* Add to Cart */}
              <Button
                size="lg"
                className="w-full"
                onClick={handleAddToCart}
                disabled={!selectedVariant || selectedVariant.stock === 0}
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Ajouter au panier
              </Button>

              {/* Features */}
              <Card className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Truck className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Livraison gratuite</p>
                    <p className="text-sm text-muted-foreground">Dès 50€ d'achat</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <RefreshCw className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Retours gratuits</p>
                    <p className="text-sm text-muted-foreground">30 jours pour changer d'avis</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Paiement sécurisé</p>
                    <p className="text-sm text-muted-foreground">Vos données sont protégées</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
